import { useRef, useState } from 'react';
import { api } from '../api';
import type { Task, TaskState, User } from '../api';
import { Link } from '../router';
import { useSession } from '../session';
import { useData } from '../useData';
import { Shell } from '../components/Shell';
import {
  Avatar, Body, Empty, Hours, Loading, SortHeader, boardOrder, hoursOf, stateColour,
  usePriorityName, useSort, useStateName, useStatusName, when,
} from '../components/bits';
import { RichText } from '../components/RichText';
import type { RichTextHandle } from '../components/RichText';
import { ProjectForm } from '../components/ProjectForm';
import { TagSelect } from '../components/TagSelect';
import { methodName, money } from './Billing';

type Tab = 'tasks' | 'time' | 'about';

/** One project: what it is, what is open on it, and where the hours went. */
export function Project({ id }: { id: number }) {
  const { t, locale, user } = useSession();
  const statusName = useStatusName();
  // Which tab opens is decided by the project, not by a default: a project
  // with no tasks opened on an empty board, and the hours — the thing that
  // project actually has — were one click away for no reason. Once somebody
  // picks a tab, their choice wins.
  const [chosen, setChosen] = useState<Tab | null>(null);
  const [editing, setEditing] = useState(false);

  const project = useData(() => api.project(id), [id]);
  const tasks = useData(() => api.projectTasks(id), [id]);
  const time = useData(() => api.projectTime(id), [id]);
  const files = useData(() => api.projectFiles(id), [id]);
  const people = useData(() => api.projectPeople(id), [id]);
  const { sorted: sortedTime, header: timeHeader } = useSort(time.data, {
    date: { value: (e) => e.workedOn, first: 'desc' },
    what: { value: (e) => e.title },
    person: { value: (e) => e.userName },
    hours: { value: (e) => e.minutes, first: 'desc' },
  });

  const tab: Tab = chosen ?? (tasks.data && tasks.data.length === 0 ? 'time' : 'tasks');
  const setTab = setChosen;

  if (project.error) return <Shell><Empty>{t.notFound}</Empty></Shell>;
  if (!project.data) return <Shell><Loading /></Shell>;

  const p = project.data;
  const totalMinutes = time.data?.reduce((sum, e) => sum + e.minutes, 0) ?? 0;

  return (
    <Shell>
      <div className="page-head">
        <div className="grow">
          <p className="kicker">
            {p.workspaceName} · {statusName(p.status)}
            {user?.isStaff && ` · ${methodName(t, p.billingMethod, p.billingPeriodMonths)}`}
            {user?.isStaff && p.hourlyRateCents != null &&
              ` · ${money(p.hourlyRateCents, locale)}${t.perHour}`}
          </p>
          <h1>{p.title}</h1>
        </div>
        <div style={{ minWidth: 200 }}>
          <Hours used={p.usedHours} total={p.totalHours} />
          {user?.isStaff && (
            <p style={{ margin: '8px 0 0' }}>
              <button onClick={() => setEditing((v) => !v)}>
                {editing ? t.cancel : t.editProject}
              </button>
            </p>
          )}
        </div>
      </div>

      {editing && (
        <ProjectForm
          project={p}
          onDone={() => { setEditing(false); project.reload(); people.reload(); }}
          onCancel={() => setEditing(false)}
        />
      )}

      <div className="tabs" role="tablist">
        <button role="tab" aria-current={tab === 'tasks' ? 'true' : undefined} onClick={() => setTab('tasks')}>
          {t.tasks}
        </button>
        <button role="tab" aria-current={tab === 'time' ? 'true' : undefined} onClick={() => setTab('time')}>
          {t.time}
        </button>
        <button role="tab" aria-current={tab === 'about' ? 'true' : undefined} onClick={() => setTab('about')}>
          {t.about}
        </button>
      </div>

      {/* The board gets the whole page. Squeezing it beside a sidebar cost it
          two columns and cut the cards in half, and nothing in that sidebar is
          worth a column of tickets. */}
      {tab === 'tasks' && (
        <Board
          projectId={id}
          tasks={tasks.data}
          loading={tasks.loading}
          canCreate={p.status === 'in_progress'}
          onChanged={tasks.reload}
        />
      )}

      {tab === 'time' && (
        <>
          {time.loading && !time.data ? <Loading /> : null}
          {time.data?.length === 0 ? <Empty>{t.noTime}</Empty> : null}
          {time.data && time.data.length > 0 && (
            <table>
              <thead>
                <tr>
                  <SortHeader className="date" {...timeHeader('date')}>{t.date}</SortHeader>
                  <SortHeader {...timeHeader('what')}>{t.what}</SortHeader>
                  {user?.isStaff && <SortHeader {...timeHeader('person')}>{t.person}</SortHeader>}
                  <SortHeader className="num" {...timeHeader('hours')}>{t.hours}</SortHeader>
                </tr>
              </thead>
              <tbody>
                {sortedTime.map((e) => (
                  <tr key={e.id}>
                    <td className="muted date">{when(e.workedOn, locale)}</td>
                    <td>{e.title ?? ''}</td>
                    {user?.isStaff && <td className="muted">{e.userName ?? ''}</td>}
                    <td className="num">{hoursOf(e.minutes)}</td>
                  </tr>
                ))}
                <tr className="sum">
                  <td colSpan={user?.isStaff ? 3 : 2}>{t.total}</td>
                  <td className="num">{hoursOf(totalMinutes)}</td>
                </tr>
              </tbody>
            </table>
          )}
          <p style={{ marginTop: 'var(--sp-4)' }}>
            <a className="link" href={api.timeCsvUrl({ projectId: id })}>{t.download}</a>
          </p>
        </>
      )}

      {tab === 'about' && (
        <div className="split">
          <div>
            <Body html={p.descriptionHtml} />
            {!p.descriptionHtml && <Empty>{t.noProjects}</Empty>}
          </div>
          <aside className="side">
            <dl>
              {p.managerName && (<><dt>{t.manager}</dt><dd>{p.managerName}</dd></>)}
              {p.startDate && (<><dt>{t.started}</dt><dd>{when(p.startDate, locale)}</dd></>)}
            </dl>
            <ProjectPeople
              projectId={id}
              people={people.data}
              canEdit={user?.isStaff ?? false}
              onChanged={people.reload}
            />
            {files.data && files.data.length > 0 && (
              <section>
                <dt>{t.files}</dt>
                <ul className="files">
                  {files.data.map((f) => (
                    <li key={f.id}>
                      <a href={`/api/attachments/${f.id}`}>{f.filename}</a>
                    </li>
                  ))}
                </ul>
              </section>
            )}
          </aside>
        </div>
      )}

    </Shell>
  );
}

/** Who is on this project, which is also who can see it.
 *
 *  Membership used to be the client's: somebody brought in for one project
 *  could read every project that client had ever had. It is the project's now,
 *  and this is where it is said. */
function ProjectPeople({
  projectId, people, canEdit, onChanged,
}: {
  projectId: number;
  people: User[] | undefined;
  canEdit: boolean;
  onChanged: () => void;
}) {
  const { t } = useSession();
  const [adding, setAdding] = useState(false);
  const everyone = useData(() => (adding ? api.people() : Promise.resolve([] as User[])), [adding]);

  const add = async (userId: number | undefined) => {
    if (userId === undefined) return;
    await api.addProjectMember(projectId, userId);
    setAdding(false);
    onChanged();
  };

  const remove = async (userId: number) => {
    if (!window.confirm(t.confirmRemovePerson)) return;
    await api.removeProjectMember(projectId, userId);
    onChanged();
  };

  const here = new Set((people ?? []).map((u) => u.id));

  return (
    <section>
      <dt>{t.people}</dt>
      <ul className="people">
        {(people ?? []).map((u) => (
          <li key={u.id}>
            <Avatar name={u.name} />
            <span className="grow">{u.name}</span>
            {canEdit && (
              <button onClick={() => void remove(u.id)} title={t.remove}>&times;</button>
            )}
          </li>
        ))}
        {people?.length === 0 && <li className="muted">{t.noPeople}</li>}
      </ul>
      {canEdit && (adding ? (
        <TagSelect
          id={`add-person-${projectId}`}
          value={undefined}
          placeholder={t.search}
          options={(everyone.data ?? [])
            .filter((u) => !here.has(u.id))
            .map((u) => ({ value: u.id, label: u.name }))}
          onChange={(v) => void add(v)}
        />
      ) : (
        <p><button onClick={() => setAdding(true)}>{t.addPerson}</button></p>
      ))}
    </section>
  );
}

/** The board: a column per state, in the order work moves through them.
 *
 *  There is no dragging yet. A card carries the same dropdown the task page
 *  has, which is the part that actually moves work along. */
function Board({
  projectId, tasks, loading, canCreate, onChanged,
}: {
  projectId: number;
  tasks: Task[] | undefined;
  loading: boolean;
  canCreate: boolean;
  onChanged: () => void;
}) {
  const { t, user } = useSession();
  const stateName = useStateName();
  const priorityName = usePriorityName();
  const [creating, setCreating] = useState(false);

  // Dragging is the studio's; a client moves a task with the Approve button on
  // the task itself, which says what it does. It is also not a keyboard path,
  // so the dropdown on the task page stays the way everything can be done.
  const canDrag = user?.isStaff ?? false;

  const [dragging, setDragging] = useState<number | null>(null);
  const [over, setOver] = useState<TaskState | null>(null);
  // Where cards have been put, before the server has confirmed it. Without
  // this the card snaps back to its old column until the reload lands.
  const [moved, setMoved] = useState<Record<number, TaskState>>({});
  const [failed, setFailed] = useState<string | null>(null);

  if (loading && !tasks) return <Loading />;

  const placed = (task: Task): TaskState => moved[task.id] ?? task.state;

  const drop = async (state: TaskState) => {
    const id = dragging;
    setDragging(null);
    setOver(null);
    if (id === null) return;

    const task = (tasks ?? []).find((candidate) => candidate.id === id);
    if (!task || placed(task) === state) return;

    setMoved((m) => ({ ...m, [id]: state }));
    setFailed(null);
    try {
      await api.updateTask(id, { state });
      onChanged();
      setMoved((m) => {
        const next = { ...m };
        delete next[id];
        return next;
      });
    } catch (err) {
      // Put it back where it was and say so, rather than leaving the board
      // showing something the database does not agree with.
      setMoved((m) => {
        const next = { ...m };
        delete next[id];
        return next;
      });
      setFailed(err instanceof Error ? err.message : String(err));
    }
  };

  const columns = boardOrder
    .map((state) => ({ state, items: (tasks ?? []).filter((task) => placed(task) === state) }))
    .filter((column) => column.items.length > 0 || column.state !== 'closed');

  return (
    <>
      <div className="between" style={{ marginBottom: 'var(--sp-4)' }}>
        <span className="muted">{t.taskCount(tasks?.length ?? 0)}</span>
        {canCreate && (
          <button className="primary" onClick={() => setCreating(true)}>{t.newTask} &gt;</button>
        )}
      </div>

      {creating && (
        <NewTask
          projectId={projectId}
          onDone={() => { setCreating(false); onChanged(); }}
          onCancel={() => setCreating(false)}
        />
      )}

      {tasks?.length === 0 && !creating ? <Empty>{t.noTasks}</Empty> : null}
      {failed && <p className="error" role="alert">{failed}</p>}

      <div className="board">
        {columns.map(({ state, items }) => (
          <div
            className={[
              'column',
              state === 'closed' ? 'spent' : '',
              items.length === 0 ? 'quiet' : '',
              over === state ? 'over' : '',
            ].filter(Boolean).join(' ')}
            key={state}
            style={{ ['--state' as string]: stateColour[state] }}
            onDragOver={canDrag ? (e) => { e.preventDefault(); setOver(state); } : undefined}
            onDragLeave={canDrag ? () => setOver((o) => (o === state ? null : o)) : undefined}
            onDrop={canDrag ? (e) => { e.preventDefault(); void drop(state); } : undefined}
          >
            <header>
              <span className="name">{stateName(state)}</span>
              <span className="muted">{items.length}</span>
            </header>
            {items.map((task) => (
              <Link
                to={`/tasks/${task.id}`}
                className={dragging === task.id ? 'ticket lifted' : 'ticket'}
                key={task.id}
                draggable={canDrag}
                onDragStart={canDrag ? (e) => {
                  setDragging(task.id);
                  e.dataTransfer.effectAllowed = 'move';
                  // Firefox will not start a drag without payload.
                  e.dataTransfer.setData('text/plain', String(task.id));
                } : undefined}
                onDragEnd={canDrag ? () => { setDragging(null); setOver(null); } : undefined}
              >
                <span className="title">{task.title}</span>
                <span className="foot">
                  <span className={`pill ${task.priority}`}>{priorityName(task.priority)}</span>
                  {task.assigneeName ? <Avatar name={task.assigneeName} /> : <span />}
                </span>
              </Link>
            ))}
          </div>
        ))}
      </div>
    </>
  );
}

function NewTask({ projectId, onDone, onCancel }: { projectId: number; onDone: () => void; onCancel: () => void }) {
  const { t } = useSession();
  const priorityName = usePriorityName();
  const [title, setTitle] = useState('');
  const description = useRef<RichTextHandle>(null);
  const [priority, setPriority] = useState<'low' | 'medium' | 'high'>('medium');
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setBusy(true);
    try {
      await api.createTask({
        projectId,
        title,
        descriptionHtml: description.current?.html() || undefined,
        priority,
      });
      onDone();
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="card stack" style={{ marginBottom: 'var(--sp-4)' }}>
      <div>
        <label htmlFor="title">{t.title}</label>
        <input id="title" required autoFocus value={title} onChange={(e) => setTitle(e.target.value)} />
      </div>
      <div>
        <label id="desc-label">{t.description}</label>
        <RichText
          ref={description} labelledBy="desc-label"
          owner={{ type: 'project', id: projectId }} onPendingChange={setUploading}
        />
      </div>
      <div style={{ maxWidth: 200 }}>
        <label htmlFor="prio">{t.priority}</label>
        <select id="prio" value={priority} onChange={(e) => setPriority(e.target.value as 'low' | 'medium' | 'high')}>
          {(['low', 'medium', 'high'] as const).map((p) => (
            <option key={p} value={p}>{priorityName(p)}</option>
          ))}
        </select>
      </div>
      <div className="row">
        <button className="primary" type="submit" disabled={busy || uploading || !title}>{t.createTask} &gt;</button>
        <button type="button" onClick={onCancel}>{t.back}</button>
      </div>
    </form>
  );
}

export type { TaskState };
